import {
  BankMatchObjectSourceValue,
  BankMatchRejectionReasonValue,
  BankMatchUiFlowValue,
  UiSourceValue,
} from '@features/reconciliation/enums'
import { getBankMatchObjectSource, getUiSourceTrackingHeaders } from '@features/reconciliation/utils'
import {
  BankLine,
  BankLineMatch,
  BankLineSubjectAssociation,
  DifferenceType,
  ReconciliationSuggestion,
  ReconciliationSuggestionsResponse,
} from '@modules-deprecated/bankReconciliation/types'

import { deleteRequest, getRequest, postRequest, putRequest } from '../../utils'
import { PayableSubjectType } from './types/payableSubjectType'
import { getReferenceFromSubject } from './utils'

export interface ReconcilableResponse {
  bankLines?: BankLine[]
  bankLineMatches?: BankLineMatch[]
  [key: string]: unknown
}

export interface ReconciliationSubjectReference {
  id: string
  type: PayableSubjectType
}

export type ReconcilableParameters = {
  differenceType?: DifferenceType
  expectedAmount?: number
  isAutoMatch?: boolean
}

export interface CreateSubjectsAndBankLinesReconcilationOptions {
  bankLinesIds: string[]
  organizationId: string
  parameters?: ReconcilableParameters
  subjects: string[]
  uiSource?: UiSourceValue
  uiFlow?: BankMatchUiFlowValue
}

export const createSubjectsAndBankLinesReconcilation = ({
  bankLinesIds,
  organizationId,
  parameters,
  subjects,
  uiSource,
  uiFlow,
}: CreateSubjectsAndBankLinesReconcilationOptions): Promise<ReconcilableResponse> => {
  return postRequest(
    `/v2/organizations/${organizationId}/reconcilable`,
    {
      reconcilable: {
        bankLines: bankLinesIds,
        parameters,
        subjects,
      },
    },
    getUiSourceTrackingHeaders({
      uiSource,
      uiFlow,
      objectSource: getBankMatchObjectSource(subjects),
    }),
  )
}

interface GetSubjectReconciliationMatchesPayload {
  organizationId: string
  subjectId: string
  subjectType?: PayableSubjectType
}

export const getSubjectReconciliationMatches = ({
  organizationId,
  subjectId,
  subjectType,
}: GetSubjectReconciliationMatchesPayload): Promise<ReconciliationSuggestionsResponse> => {
  return getRequest(`/api/matching-service/${organizationId}/matches/reconcilable/${subjectType}/${subjectId}`)
}

export interface DeleteSubjectReconciliationMatchPayload {
  organizationId: string
  suggestion: ReconciliationSuggestion
  uiSource?: UiSourceValue
  rejectionReason?: BankMatchRejectionReasonValue
}

export const deleteSubjectReconciliationMatch = ({
  organizationId,
  suggestion,
  uiSource,
  rejectionReason,
}: DeleteSubjectReconciliationMatchPayload): Promise<void> => {
  return postRequest(`/api/matching-service/${organizationId}/matches/reject`, suggestion, {
    ...getUiSourceTrackingHeaders({ uiSource }),
    ...(rejectionReason && { 'X-Rejection-Reason': rejectionReason }),
  })
}

export interface CreateSubjectReconciliationMatchPayload {
  subjectId: string
  subjectType: PayableSubjectType
  defaultBankFeeAccountId?: string | null
  differenceType?: DifferenceType
  isAutoMatch?: boolean
  matchId: string
  uiSource?: UiSourceValue
  uiFlow?: BankMatchUiFlowValue
}

interface CreateSubjectAssociationResponse {
  bankLineSubjectAssociations: BankLineSubjectAssociation[]
}

export const createSubjectAssociation = (
  matchId: string,
  subjectReference: string,
): Promise<CreateSubjectAssociationResponse> => {
  return postRequest('/v2/bankLineSubjectAssociations', {
    bankLineSubjectAssociation: {
      matchId,
      subjectReference,
    },
  })
}

export const deleteSubjectAssociation = (associationId: string): Promise<void> => {
  return deleteRequest(`/v2/bankLineSubjectAssociations/${associationId}`)
}

export const approveBankLineMatch = (
  matchId: string,
  differenceType?: DifferenceType,
  defaultBankFeeAccountId?: string | null,
  isAutoMatch?: boolean,
  uiSource?: UiSourceValue,
  uiFlow?: BankMatchUiFlowValue,
  objectSource?: BankMatchObjectSourceValue,
): Promise<ReconcilableResponse> => {
  return putRequest(
    `/v2/bankLineMatches/${matchId}`,
    {
      bankLineMatch: {
        isApproved: true,
        differenceType,
        isAutoMatch: !!isAutoMatch,
        ...(differenceType === 'bankFee' && defaultBankFeeAccountId && { feeAccountId: defaultBankFeeAccountId }),
      },
    },
    getUiSourceTrackingHeaders({ uiSource, uiFlow, objectSource }),
  )
}

export const createSubjectReconciliationMatch = async ({
  subjectId,
  subjectType,
  defaultBankFeeAccountId,
  differenceType,
  isAutoMatch,
  matchId,
  uiSource,
  uiFlow,
}: CreateSubjectReconciliationMatchPayload): Promise<ReconcilableResponse> => {
  await createSubjectAssociation(matchId, getReferenceFromSubject(subjectId, subjectType))
  return approveBankLineMatch(
    matchId,
    differenceType,
    defaultBankFeeAccountId,
    isAutoMatch,
    uiSource,
    uiFlow,
    getBankMatchObjectSource([subjectType]),
  )
}
